// src/pages/Signup.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import CustomAlert from "../components/CustomAlert";
import { UserPlus, AlertCircle, ArrowLeft, CalendarDays } from 'lucide-react';
import logo from "../assets/images/apemlogo.png";

if (!document.getElementById('spinner-keyframes')) {
    const keyframes = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    `;
    const styleSheet = document.createElement("style");
    styleSheet.id = 'spinner-keyframes';
    styleSheet.type = "text/css";
    styleSheet.innerText = keyframes;
    document.head.appendChild(styleSheet);
}

export default function Signup() {
    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [phone, setPhone] = useState("");
    const [birthDate, setBirthDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: "", type: "error" });
    const [barId, setBarId] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const returnPath = location.state?.from
        ? `${location.state.from.pathname || '/home'}${location.state.from.search || ''}`
        : '/home';

    useEffect(() => {
        const barIdFromUrl = searchParams.get('bar_id');
        if (barIdFromUrl) {
            setBarId(barIdFromUrl);
            console.log('Signup: bar_id detectado na URL:', barIdFromUrl);
        }
    }, [searchParams]);

    const validateEmail = (emailToValidate) => {
        const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return re.test(String(emailToValidate).toLowerCase());
    };

    const normalizePhoneDigits = (value) => {
        let digits = value.replace(/\D/g, "");

        if (digits.startsWith('55') && digits.length > 11) {
            digits = digits.slice(2);
        }

        if (digits.length > 11) {
            digits = digits.slice(0, 11);
        }

        return digits;
    };

    const formatPhone = (digits) => {
        if (!digits) return "";

        if (digits.length <= 2) {
            return `(${digits}`;
        }

        if (digits.length <= 7) {
            return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
        }

        if (digits.length <= 10) {
            return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
        }

        return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    };

    const handlePhoneChange = (e) => {
        const digits = normalizePhoneDigits(e.target.value);
        setPhone(formatPhone(digits));
    };

    const formatBirthDate = (value) => {
        const digits = value.replace(/\D/g, "").slice(0, 8);

        if (digits.length <= 2) return digits;
        if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
        return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    };

    const handleBirthDateChange = (e) => {
        setBirthDate(formatBirthDate(e.target.value));
    };

    const parseBirthDate = (value) => {
        const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

        if (!match) return null;

        const day = Number(match[1]);
        const month = Number(match[2]);
        const year = Number(match[3]);

        if (month < 1 || month > 12) return null;
        if (day < 1 || day > 31) return null;
        if (year < 1900) return null;

        const date = new Date(year, month - 1, day);

        if (
            date.getFullYear() !== year ||
            date.getMonth() !== month - 1 ||
            date.getDate() !== day
        ) {
            return null;
        }

        return {
            date,
            formattedApi: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        };
    };

    const handleSignup = async () => {
        setError(null);

        if (!username || !email || !password || !phone || !birthDate) {
            setError("Todos os campos são obrigatórios.");
            return;
        }

        if (!validateEmail(email)) {
            setError("Por favor, insira um e-mail válido.");
            return;
        }

        if (password.length < 6) {
            setError("A senha deve ter pelo menos 6 caracteres.");
            return;
        }

        if (username.length < 4) {
            setError("O nome deve ter pelo menos 4 caracteres.");
            return;
        }

        const justDigitsPhone = normalizePhoneDigits(phone);
        if (justDigitsPhone.length < 10 || justDigitsPhone.length > 11) {
            setError("Número de telefone inválido. Informe DDD e número.");
            return;
        }

        const parsedBirthDate = parseBirthDate(birthDate);

        if (!parsedBirthDate) {
            setError("Informe uma data de nascimento válida no formato DD/MM/AAAA.");
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        parsedBirthDate.date.setHours(0, 0, 0, 0);

        if (parsedBirthDate.date >= today) {
            setError("Informe uma data de nascimento válida.");
            return;
        }

        setLoading(true);
        console.log("Signup: Attempting registration for:", email);

        const userData = {
            name: username.trim().replace(/\s+/g, ' '),
            email: email.toLowerCase().trim(),
            password,
            phone: justDigitsPhone,
            birth_date: parsedBirthDate.formattedApi,
        };

        try {
            const response = await axios.post("https://apem.riderup.uk/api/register", userData);

            console.log("Signup: API Response Status:", response.status);
            console.log("Signup: API Response Data:", response.data);

            if (response.status === 200 || response.status === 201) {
                if (response.data?.token) {
                    localStorage.setItem("token", response.data.token);

                    if (barId) {
                        try {
                            await axios.post(
                                "https://apem.riderup.uk/api/addBar",
                                { client_id: barId },
                                { headers: { Authorization: `Bearer ${response.data.token}` } }
                            );
                        } catch (barError) {
                            console.error("Signup: Erro ao conectar ao bar:", barError);
                        }
                    }

                    window.location.href = returnPath;
                    return;
                }

                setAlertInfo({ show: true, message: "Cadastro realizado com sucesso! Faça o login para continuar.", type: 'success' });
                setTimeout(() => navigate("/"), 2000);
            } else {
                const errorMessage = response.data?.message || response.data?.error || "Erro inesperado durante o cadastro.";
                setError(errorMessage);
            }
        } catch (error) {
            console.error("Signup: API Error:", error);
            let errorMessage = "Falha ao cadastrar. Verifique os dados e tente novamente.";
            if (axios.isAxiosError(error) && error.response) {
                if (error.response.data?.errors) {
                    const firstError = Object.values(error.response.data.errors)[0];
                    if (Array.isArray(firstError) && firstError[0]) {
                        errorMessage = firstError[0];
                    } else {
                        errorMessage = error.response.data?.message || `Erro ${error.response.status}`;
                    }
                } else {
                    errorMessage = error.response.data?.message || error.response.data?.error || `Erro ${error.response.status}`;
                }
            } else if (error instanceof Error) {
                errorMessage = error.message;
            }
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitByEnter = (e) => {
        if (e.key === 'Enter') {
            handleSignup();
        }
    };

    const handleCloseAlert = () => setAlertInfo({ ...alertInfo, show: false });

    return (
        <div style={styles.container}>
            {alertInfo.show && <CustomAlert message={alertInfo.message} type={alertInfo.type} onclose={handleCloseAlert} />}
            <img src={logo} alt="Logo A Próxima é Minha" style={styles.logo} />
            <h2 style={styles.title}>Criar Conta</h2>

            {barId && (
                <div style={styles.qrCodeMessage}>
                    <AlertCircle size={18} color="#6a0dad" style={{ marginRight: '8px' }} />
                    <span>Você será conectado automaticamente ao estabelecimento após o cadastro.</span>
                </div>
            )}

            <input
                type="text"
                name="username"
                placeholder="Nome completo"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                style={styles.input}
                aria-label="Nome Completo"
                disabled={loading}
                autoComplete="name"
            />

            <input
                type="email"
                name="email"
                placeholder="Seu melhor e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={styles.input}
                aria-label="Email"
                disabled={loading}
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="email"
            />

            <input
                type="password"
                name="password"
                placeholder="Crie uma senha (mín. 6 caracteres)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={styles.input}
                aria-label="Senha"
                disabled={loading}
                autoComplete="new-password"
            />

            <div style={styles.phoneWrapper}>
                <span style={styles.phonePrefix}>+55</span>
                <input
                    type="tel"
                    name="phone"
                    placeholder="(63) 99999-9999"
                    value={phone}
                    onChange={handlePhoneChange}
                    onKeyDown={handleSubmitByEnter}
                    required
                    maxLength={16}
                    style={styles.phoneInput}
                    aria-label="Telefone Celular"
                    inputMode="tel"
                    disabled={loading}
                    autoComplete="tel"
                />
            </div>

            <div style={styles.dateWrapper}>
                <CalendarDays size={18} style={styles.dateIcon} />
                <input
                    type="text"
                    name="birth_date"
                    placeholder="Data de nascimento"
                    value={birthDate}
                    onChange={handleBirthDateChange}
                    onKeyDown={handleSubmitByEnter}
                    required
                    style={styles.dateInput}
                    aria-label="Data de nascimento"
                    disabled={loading}
                    autoComplete="bday"
                    inputMode="numeric"
                    maxLength={10}
                />
            </div>

            {error && (
                <div style={styles.errorBanner}>
                    <AlertCircle size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                    <span>{error}</span>
                </div>
            )}

            <button
                onClick={handleSignup}
                disabled={loading}
                style={styles.buttonPrimary(loading)}
            >
                {loading ? (
                    <>
                        <div style={styles.buttonSpinner}></div> Cadastrando...
                    </>
                ) : (
                    <>
                        <UserPlus size={18} /> Criar Conta
                    </>
                )}
            </button>

            <button
                style={styles.linkButton}
                onClick={() => navigate("/")}
                disabled={loading}
            >
                <ArrowLeft size={14} style={{ marginRight: '4px' }} />
                Já tem conta? <span style={styles.loginLink}>Entrar</span>
            </button>
        </div>
    );
}

const styles = {
    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#12121f",
        minHeight: "100vh",
        padding: '30px 20px',
        boxSizing: 'border-box',
    },
    logo: {
        width: 'auto',
        height: 'auto',
        maxHeight: "80px",
        maxWidth: '70%',
        marginBottom: 25,
        objectFit: "contain",
    },
    title: {
        fontSize: 28,
        fontWeight: "bold",
        color: "#f0f0f0",
        marginBottom: 15,
        textAlign: 'center',
    },
    qrCodeMessage: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(106, 13, 173, 0.15)',
        border: '1px solid rgba(106, 13, 173, 0.3)',
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: 20,
        maxWidth: '400px',
        width: '100%',
        fontSize: '14px',
        color: '#e0e0e0',
        textAlign: 'center',
        boxSizing: 'border-box',
    },
    input: {
        width: "100%",
        maxWidth: "400px",
        padding: "14px 18px",
        margin: "8px 0",
        border: "1px solid #4a4a6e",
        borderRadius: "8px",
        backgroundColor: "#1a1a2e",
        color: "#f0f0f0",
        fontSize: "16px",
        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.3)",
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s ease',
    },
    phoneWrapper: {
        width: '100%',
        maxWidth: '400px',
        display: 'flex',
        alignItems: 'center',
        margin: '8px 0',
        border: '1px solid #4a4a6e',
        borderRadius: '8px',
        backgroundColor: '#1a1a2e',
        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        boxSizing: 'border-box',
    },
    phonePrefix: {
        padding: '14px 14px 14px 16px',
        color: '#cfcfe6',
        fontSize: '16px',
        fontWeight: '600',
        borderRight: '1px solid #30304d',
        backgroundColor: '#171728',
    },
    phoneInput: {
        flex: 1,
        padding: '14px 18px',
        border: 'none',
        backgroundColor: 'transparent',
        color: '#f0f0f0',
        fontSize: '16px',
        outline: 'none',
        minWidth: 0,
    },
    dateWrapper: {
        width: '100%',
        maxWidth: '400px',
        display: 'flex',
        alignItems: 'center',
        margin: '8px 0',
        border: '1px solid #4a4a6e',
        borderRadius: '8px',
        backgroundColor: '#1a1a2e',
        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.3)',
        boxSizing: 'border-box',
    },
    dateIcon: {
        marginLeft: '16px',
        color: '#cfcfe6',
        flexShrink: 0,
    },
    dateInput: {
        width: '100%',
        padding: '14px 18px 14px 12px',
        border: 'none',
        backgroundColor: 'transparent',
        color: '#f0f0f0',
        fontSize: '16px',
        outline: 'none',
        boxSizing: 'border-box',
    },
    buttonBase: {
        width: "100%",
        maxWidth: "400px",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        padding: "12px",
        borderRadius: "8px",
        fontSize: "16px",
        fontWeight: 'bold',
        border: "none",
        cursor: "pointer",
        transition: 'background-color 0.3s ease, opacity 0.3s ease',
        boxSizing: 'border-box',
    },
    buttonPrimary: (isLoading) => ({
        ...styles.buttonBase,
        backgroundColor: isLoading ? '#5a0ca0' : '#6a0dad',
        color: "white",
        marginTop: 20,
        opacity: isLoading ? 0.7 : 1,
        cursor: isLoading ? "not-allowed" : "pointer",
    }),
    linkButton: {
        color: "#a0a0b0",
        background: "none",
        border: "none",
        marginTop: 20,
        fontSize: 14,
        cursor: "pointer",
        padding: '5px',
        textAlign: 'center',
        transition: 'color 0.2s ease',
        display: 'inline-flex',
        alignItems: 'center',
    },
    loginLink: {
        color: "#c0c0ff",
        fontWeight: "bold",
        textDecoration: 'underline',
        marginLeft: '4px',
    },
    errorBanner: {
        width: '100%',
        maxWidth: "400px",
        backgroundColor: 'rgba(255, 96, 96, 0.1)',
        border: '1px solid rgba(255, 96, 96, 0.3)',
        color: '#ff8080',
        padding: '10px 15px',
        borderRadius: '8px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        textAlign: 'left',
        marginTop: '15px',
        marginBottom: '-5px',
        boxSizing: 'border-box',
    },
    buttonSpinner: {
        border: '3px solid rgba(255, 255, 255, 0.3)',
        borderLeftColor: '#fff',
        borderRadius: '50%',
        width: '16px',
        height: '16px',
        animation: 'spin 1s linear infinite',
        display: 'inline-block',
    },
};
